<script setup lang="ts">
import { onBeforeMount, nextTick, ref, watch, computed } from 'vue';
import { cloneDeep, pickBy } from 'lodash-es';
import { useTemplateRefsList } from '@vueuse/core';
import { useBrokerLoanAppProcess } from '@/helpers/useBrokerLoanAppProcess';
import type { ApplicantId, Applicant } from '@/types';
import type { BorrowerGroup } from '@/types/BorrowerGroup';
import ExpensesForm from './modules/Expenses/ExpensesForm.vue';
import { getFullName } from '@/helpers/format';
import api from '@/api';
import type { ExpenseTypeDto } from '@/api/backend/public/expense-types/expense-type.dto';
import { useApplicationApi } from '@/helpers/useApplicationApi';
import { DraftEventName } from '@/helpers/draftEvents';
import { useHouseholdApplicants } from '@/helpers/useHouseholdApplicants';
import { useHouseholdMessages } from '@/helpers/useHouseholdMessages';
import { BorrowerType } from '@/helpers/const';

const { authorisedUser, loanApp, loanApplicants, state, saveAndGoNext, goBack } = useBrokerLoanAppProcess();
const { saveApplicationToDraft } = useApplicationApi();

const isReady = ref(false);
const isLoading = ref(false);
const expenseTypes = ref<ExpenseTypeDto[]>([]);

const isSecondaryDeclaration = false;
const isIncomeExpenseRequest = false;

const hasNonIndividualBorrower = computed(
  () =>
    loanApp.value.borrowerGroups?.some((g) => g.type === BorrowerType.COMPANY || g.type === BorrowerType.TRUST) ??
    false,
);

const showCommercialExpenseUI = computed(() => hasNonIndividualBorrower.value);

const getApplicantsFromGroup = (group: BorrowerGroup): ApplicantId[] =>
  group.applicants?.map((a) => a.applicantId as ApplicantId) ?? [];

/**
 * Get all applicants eligible for expense collection.
 * For Regulated scenarios: Collect from ALL directors/trustees/individuals (no skip).
 * For Unregulated scenarios: Expenses step is skipped entirely (handled by state machine).
 */
const eligibleExpenseApplicants = computed<ApplicantId[]>(() => {
  if (!showCommercialExpenseUI.value) return [];

  // For Regulated + End Debt: collect expenses from ALL applicants in all borrower groups
  // No skip logic - directors of trading companies are included
  const ids = (loanApp.value.borrowerGroups ?? []).flatMap(getApplicantsFromGroup);

  return [...new Set(ids)]; // Deduplicates applicant IDs
});

const isMarried = (applicant: Applicant): boolean =>
  applicant.relationshipStatus === 'married_or_defacto' &&
  applicant.relatedApplicantIndex !== undefined &&
  applicant.relatedApplicantIndex >= 0;

const getPartnerId = (applicant: Applicant): ApplicantId | undefined => {
  if (applicant.relatedApplicantIndex === undefined || applicant.relatedApplicantIndex < 0) return undefined;
  return loanApp.value.applicants[applicant.relatedApplicantIndex]?.id as ApplicantId | undefined;
};

const hasEligiblePartner = (applicant: Applicant, eligible: ApplicantId[]): boolean =>
  isMarried(applicant) && eligible.includes(getPartnerId(applicant) as ApplicantId);

/**
 * Apply household grouping to eligible applicants
 * Married couples among eligible applicants share one expense form
 */
const commercialHouseholdExpenseApplicants = computed<ApplicantId[]>(() => {
  if (!showCommercialExpenseUI.value) return [];

  const eligible = eligibleExpenseApplicants.value;
  if (eligible.length === 0) return [];

  const eligibleApplicants = loanApp.value.applicants.filter((a) => eligible.includes(a.id as ApplicantId));
  const hasMarriedPairs = eligibleApplicants.some((a) => hasEligiblePartner(a, eligible));

  if (!hasMarriedPairs) return eligible;

  // Group married couples using reduce - first in pair becomes household head
  return eligibleApplicants.reduce<{ heads: ApplicantId[]; processed: Set<ApplicantId> }>(
    (acc, applicant) => {
      const applicantId = applicant.id as ApplicantId;
      if (acc.processed.has(applicantId)) return acc;

      acc.processed.add(applicantId);
      acc.heads.push(applicantId);

      const partnerId = getPartnerId(applicant);
      if (hasEligiblePartner(applicant, eligible) && partnerId) {
        acc.processed.add(partnerId);
      }

      return acc;
    },
    { heads: [], processed: new Set() },
  ).heads;
});

const applicantCompletionStatuses = ref<{ id: ApplicantId; status: 'completed' | 'incomplete' }[]>([]);

const { primaryPartner, otherApplicants, authorisedUserId, applicantsMarried, householdValidApplicants } =
  useHouseholdApplicants(loanApp, authorisedUser, { isSecondaryDeclaration, isIncomeExpenseRequest });

const { expensesMessage } = useHouseholdMessages(
  { primaryPartner, otherApplicants, authorisedUserId },
  { isSecondaryDeclaration, isIncomeExpenseRequest },
);

/** Get the list of applicants to display based on flow type */
const displayApplicants = computed<ApplicantId[]>(() =>
  showCommercialExpenseUI.value ? commercialHouseholdExpenseApplicants.value : householdValidApplicants.value,
);

const addCompletionStatus = (applicantIds: ApplicantId[]) =>
  applicantIds.map((applicantId) => ({
    applicantId,
    completionStatus: applicantCompletionStatuses.value.find((s) => s.id === applicantId)?.status ?? 'incomplete',
  }));

const householdValidApplicantsExpenses = computed(() => addCompletionStatus(householdValidApplicants.value));
const commercialHouseholdExpenseApplicantsWithStatus = computed(() =>
  addCompletionStatus(commercialHouseholdExpenseApplicants.value),
);

const validApplicantIds = new Set(loanApp.value.applicants.map((a) => a.id));
const financials = ref(pickBy(cloneDeep(loanApp.value.financials), (_v, id) => validApplicantIds.has(id)));
const expenseRefs = useTemplateRefsList<InstanceType<typeof ExpensesForm>>();

const activeTab = ref<ApplicantId | undefined>(
  displayApplicants.value.length === 1 ? displayApplicants.value[0] : undefined,
);

const isCompleted = (id: ApplicantId) => financials.value[id]?.expenses?.numberOfDependants !== undefined;

const findFirstInvalidIndex = async (refs: (typeof expenseRefs.value)[number][]): Promise<number> => {
  const results = await Promise.all(refs.map(async (ref) => (await ref?.update()) ?? false));
  return results.findIndex((isValid) => !isValid);
};

const validateExpenses = async (): Promise<boolean> => {
  if (isSecondaryDeclaration && applicantsMarried) return true;

  const applicantsToValidate = displayApplicants.value;
  const refs = expenseRefs.value.slice(0, applicantsToValidate.length);
  const invalidIndex = await findFirstInvalidIndex(refs);

  if (invalidIndex === -1) return true;

  activeTab.value = applicantsToValidate[invalidIndex];
  window.scrollTo(0, 0);
  return false;
};

const saveAndContinue = async () => {
  if (await validateExpenses()) {
    isLoading.value = true;
    let applicationDraftId = loanApp.value.applicationDraftId;
    try {
      if (loanApp.value.brokerId) {
        applicationDraftId = await saveApplicationToDraft(
          state.value.context,
          {
            ...loanApp.value,
            financials: financials.value,
          },
          DraftEventName.Expenses,
        );
      }
      saveAndGoNext({
        financials: financials.value,
        applicationDraftId,
      });
    } catch (error) {
      console.error('Failed to save draft:', error);
    } finally {
      isLoading.value = false;
    }
  }
};

const sortByOrder = (a: ExpenseTypeDto, b: ExpenseTypeDto) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0);

onBeforeMount(async () => {
  isReady.value = false;
  try {
    const response = await api.backend.public.listExpenseTypes(loanApp.value.structuralVersionNumber);
    expenseTypes.value = response.expenseTypes?.sort(sortByOrder) ?? [];
  } finally {
    isReady.value = true;
  }
});

/** Update completion statuses when form state changes */
watch(
  () => [isReady.value, isLoading.value, activeTab.value],
  async () => {
    if (!isReady.value || isLoading.value) return;

    await nextTick();

    const applicants = displayApplicants.value;
    const refs = expenseRefs.value.slice(0, applicants.length);

    const statuses = await Promise.all(
      refs.map(async (ref, i) => ({
        id: applicants[i],
        status: ((await ref?.validate()) ?? false ? 'completed' : 'incomplete') as 'completed' | 'incomplete',
      })),
    );

    applicantCompletionStatuses.value = statuses;
  },
  { immediate: true, deep: true },
);
</script>

<template>
  <div v-if="isReady" class="w-full">
    <div class="mb-4 pb-2">
      <h2 class="text-2xl font-medium">
        Please outline the borrower's
        <BxHighlight>expenses.</BxHighlight>
      </h2>
    </div>
    <div v-if="loanApp.applicants.length > 1" class="my-6">
      <p v-if="loanApp.userType === 'Broker'">
        Enter household expenses for each borrower or married couple below by clicking "Add expenses"
      </p>
      <p v-else>{{ expensesMessage }}</p>
    </div>

    <!-- Commercial Flow: Iterate over eligible applicants with household grouping -->
    <div v-if="showCommercialExpenseUI" class="my-6 grid gap-4">
      <div v-for="applicant in commercialHouseholdExpenseApplicantsWithStatus" :key="applicant.applicantId">
        <BxCard :class="applicant.applicantId !== activeTab && 'hidden'">
          <h2 class="mb-4 flex items-center text-xl font-medium">
            <span class="mr-3 hidden md:block"><BxIcon name="customer" /></span>
            {{ getFullName(loanApplicants[applicant.applicantId]) }}
          </h2>
          <ExpensesForm
            :ref="expenseRefs.set"
            v-model="financials[applicant.applicantId].expenses"
            :applicant-id="applicant.applicantId"
            :animate="applicant.applicantId === activeTab"
            :expense-types="expenseTypes"
            :show-buttons="commercialHouseholdExpenseApplicants.length > 1"
            @save="activeTab = undefined"
            @cancel="activeTab = undefined"
          />
        </BxCard>

        <BxCard :class="applicant.applicantId === activeTab && 'hidden'">
          <div class="flex items-center">
            <div class="card-details">
              <div class="flex items-center font-medium">
                <span class="mr-3 hidden md:block"><BxIcon name="customer" /></span>
                <span>{{ getFullName(loanApplicants[applicant.applicantId]) }}</span>
                <span class="ml-6 font-normal text-gray-400">({{ applicant.completionStatus }})</span>
              </div>
            </div>
            <div class="card-button">
              <BxButton
                :id="applicant.applicantId + '-add-expenses'"
                variant="secondary"
                :disabled="activeTab && activeTab !== applicant.applicantId"
                @click="activeTab = applicant.applicantId"
              >
                {{ isCompleted(applicant.applicantId) ? 'Edit' : 'Add' }} expenses
              </BxButton>
            </div>
          </div>
        </BxCard>
      </div>
    </div>

    <!-- Original Flow: Use householdValidApplicants -->
    <div v-else class="my-6 grid gap-4">
      <div v-for="applicant in householdValidApplicantsExpenses" :key="applicant.applicantId">
        <BxCard :class="applicant.applicantId !== activeTab && 'hidden'">
          <h2 class="mb-4 text-xl font-medium">
            {{ getFullName(loanApplicants[applicant.applicantId]) }}
          </h2>
          <ExpensesForm
            v-if="isIncomeExpenseRequest && applicantsMarried"
            :ref="expenseRefs.set"
            v-model="financials[loanApp.applicants[0].id].expenses"
            :applicant-id="applicant.applicantId"
            :animate="applicant.applicantId === activeTab"
            :expense-types="expenseTypes"
            :show-buttons="householdValidApplicants.length > 1"
            :disabled="isSecondaryDeclaration && applicantsMarried"
            @save="activeTab = undefined"
            @cancel="activeTab = undefined"
          />
          <ExpensesForm
            v-else
            :ref="expenseRefs.set"
            v-model="financials[applicant.applicantId].expenses"
            :applicant-id="applicant.applicantId"
            :animate="applicant.applicantId === activeTab"
            :expense-types="expenseTypes"
            :show-buttons="householdValidApplicants.length > 1"
            :disabled="isSecondaryDeclaration && applicantsMarried"
            @save="activeTab = undefined"
            @cancel="activeTab = undefined"
          />
        </BxCard>
        <BxCard :class="applicant.applicantId === activeTab && 'hidden'">
          <div class="flex items-center">
            <div class="card-details">
              <div class="font-medium">
                <span>{{ getFullName(loanApplicants[applicant.applicantId]) }}</span>
                <span class="ml-6 font-normal text-gray-400">({{ applicant.completionStatus }})</span>
              </div>
            </div>
            <div class="card-button">
              <BxButton
                variant="secondary"
                :disabled="activeTab && activeTab !== applicant.applicantId"
                @click="activeTab = applicant.applicantId"
              >
                {{ isCompleted(applicant.applicantId) ? 'Edit' : 'Add' }} expenses
              </BxButton>
            </div>
          </div>
        </BxCard>
      </div>
    </div>

    <div class="mt-6">
      <BxButton id="expenses-continue" variant="primary" :loading="isLoading" @click="saveAndContinue()">
        {{ showCommercialExpenseUI ? 'Continue' : 'Save and continue' }}
      </BxButton>
      <BxButton variant="link" class="ml-4" @click="goBack()">Back</BxButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.card-details {
  flex-grow: 1;
}

.card-button {
  flex-basis: 200px;

  text-align: right;
}
</style>
